<template>
	<div class="c-folder-select">
		<div class="c-folder-select__header">
			<InputText placeholder="Search for boards" v-model="query"></InputText>
		</div>
		<div class="c-folder-select__search" v-if="searchResults">
			<draggable
				group="board"
				tag="div"
				v-bind="dragOptions"
				:list="searchResults"
				@change="onSearchBoardChange($event, folder)"
				@start="drag = true"
				@end="onEnd"
			>
				<transition-group type="transition" :name="!drag ? 'flip-list' : null">
					<ItemBoard
						:showDropdown="false"
						:processing="processingBoard"
						:active="isBoardActive(board)"
						v-for="board in searchResults"
						:board="board"
						:key="board.id"
						:showDragger="false"
					>
					</ItemBoard>
				</transition-group>
			</draggable>
		</div>
		<div class="c-folder-select__header">
			<h6>Folders</h6>
			<span v-if="processingBoard || processingFolder" class="c-spinner"></span>
			<a href="#" class="btn btn-box">
				<svg
					@click="onFolderAdd"
					width="24"
					height="24"
					viewBox="0 0 24 24"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M12 4V12M12 12V20M12 12H4M12 12H20"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
					/>
				</svg>
			</a>
		</div>
		<div class="c-folder-select__folders">
			<draggable
				v-bind="dragOptions"
				:list="currentFolders"
				@change="onFolderChange"
				group="folder"
				handle=".handle"
				tag="ul"
				class="list-group"
				v-model="currentFolders"
				@end="onEnd"
				@start="drag = true"
			>
				<transition-group type="transition" :name="!drag ? 'flip-list' : null">
					<li
						:class="[
							'list-group-item c-folder-select__item',
							{ active: activeFolderIds.includes(folder.id) },
							{ current: isFolderCurrent(folder) },
						]"
						v-for="folder in computedFolders"
						:key="folder.id"
					>
						<header @click="onHeaderClick($event, folder)">
							<svg
								v-if="!activeFolderIds.includes(folder.id)"
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M3 5V18C3 18.5523 3.44772 19 4 19H20C20.5523 19 21 18.5523 21 18V8C21 7.44772 20.5523 7 20 7H12.5352C12.2008 7 11.8886 6.8329 11.7031 6.5547L10.2969 4.4453C10.1114 4.1671 9.79917 4 9.46482 4H4C3.44772 4 3 4.44772 3 5Z"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>
							<svg
								v-else
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M20 11V8C20 7.44772 19.5523 7 19 7H12L10.2969 4.4453C10.1114 4.1671 9.79917 4 9.46482 4H4C3.44772 4 3 4.44772 3 5V18C3 18.5523 3.44772 19 4 19H4.76481M20 11H8.73519C8.29781 11 7.91117 11.2843 7.78071 11.7017L5.71929 18.2983C5.58883 18.7157 5.20219 19 4.76481 19M20 11H21.3199C21.6572 11 21.8978 11.3272 21.7971 11.6491L19.5 19H4.76481"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>

							<!-- <svg
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M6.44721 4.10557C6.13723 3.95058 5.76909 3.96714 5.47427 4.14935C5.17945 4.33156 5 4.65342 5 5V19C5 19.3466 5.17945 19.6684 5.47427 19.8507C5.76909 20.0329 6.13723 20.0494 6.44721 19.8944L17.4487 12.8935C17.7875 12.7241 18.0015 12.3778 18.0015 11.9991C18.0015 11.6203 17.7875 11.274 17.4487 11.1046L6.44721 4.10557Z"
									fill="currentColor"
								/>
							</svg> -->

							<InputInlineText
								v-if="!folder.default"
								@input="onFolderNameUpdate($event, folder)"
								:value="folder.name"
							></InputInlineText>
							<span v-else>
								{{ folder.name }}
							</span>
							<svg
								v-if="folder.hidden"
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M10.607 4.09497C10.0598 4.16981 9.67686 4.67407 9.75171 5.22126C9.82655 5.76845 10.3308 6.15137 10.878 6.07652L10.607 4.09497ZM21.4978 11.0953L22.3571 10.5838L21.4978 11.0953ZM19.5181 14.02C19.1738 14.4519 19.2449 15.081 19.6767 15.4253C20.1086 15.7695 20.7378 15.6985 21.082 15.2666L19.5181 14.02ZM21.498 12.9043L22.3573 13.4157L21.498 12.9043ZM3.7071 2.29289C3.31657 1.90237 2.68341 1.90237 2.29288 2.29289C1.90236 2.68342 1.90236 3.31658 2.29288 3.70711L3.7071 2.29289ZM9.87867 9.87868L10.5858 9.17157L9.87867 9.87868ZM20.2929 21.7071C20.6834 22.0976 21.3166 22.0976 21.7071 21.7071C22.0976 21.3166 22.0976 20.6834 21.7071 20.2929L20.2929 21.7071ZM2.50204 11.0956L1.64273 10.5841H1.64273L2.50204 11.0956ZM7.18936 7.48108C7.65205 7.17953 7.78267 6.55998 7.48111 6.09729C7.17956 5.6346 6.56001 5.50398 6.09732 5.80554L7.18936 7.48108ZM2.5031 12.9062L3.36234 12.3947L2.5031 12.9062ZM17.9027 18.1944C18.3653 17.8928 18.496 17.2733 18.1944 16.8106C17.8929 16.3479 17.2733 16.2173 16.8106 16.5188L17.9027 18.1944ZM10.878 6.07652C14.3525 5.6013 18.0999 7.34166 20.6385 11.6068L22.3571 10.5838C19.4847 5.75792 15.0159 3.49193 10.607 4.09497L10.878 6.07652ZM21.082 15.2666C21.5347 14.6987 21.9611 14.0815 22.3573 13.4157L20.6387 12.3928C20.2871 12.9836 19.9121 13.5257 19.5181 14.02L21.082 15.2666ZM20.6385 11.6068C20.7829 11.8493 20.7828 12.1507 20.6387 12.3928L22.3573 13.4157C22.8769 12.5429 22.8765 11.4564 22.3571 10.5838L20.6385 11.6068ZM12 14C10.8954 14 9.99999 13.1046 9.99999 12H7.99999C7.99999 14.2091 9.79085 16 12 16V14ZM2.29288 3.70711L9.17156 10.5858L10.5858 9.17157L3.7071 2.29289L2.29288 3.70711ZM9.99999 12C9.99999 11.4474 10.2228 10.9488 10.5858 10.5858L9.17156 9.17157C8.4488 9.89434 7.99999 10.8957 7.99999 12H9.99999ZM9.17156 10.5858L13.4142 14.8284L14.8284 13.4142L10.5858 9.17157L9.17156 10.5858ZM13.4142 14.8284L20.2929 21.7071L21.7071 20.2929L14.8284 13.4142L13.4142 14.8284ZM13.4142 13.4142C13.0512 13.7772 12.5526 14 12 14V16C13.1043 16 14.1057 15.5512 14.8284 14.8284L13.4142 13.4142ZM2.29288 3.70711L20.2929 21.7071L21.7071 20.2929L3.7071 2.29289L2.29288 3.70711ZM3.36135 11.607C4.45475 9.77 5.77434 8.40332 7.18936 7.48108L6.09732 5.80554C4.39937 6.91217 2.87506 8.51367 1.64273 10.5841L3.36135 11.607ZM1.64386 13.4178C3.59164 16.6894 6.27059 18.7893 9.19312 19.6107C12.121 20.4335 15.212 19.948 17.9027 18.1944L16.8106 16.5188C14.5833 17.9705 12.085 18.3459 9.73422 17.6852C7.37815 17.0231 5.08928 15.2953 3.36234 12.3947L1.64386 13.4178ZM1.64273 10.5841C1.12268 11.4579 1.1249 12.5461 1.64386 13.4178L3.36234 12.3947C3.21748 12.1514 3.21772 11.8483 3.36135 11.607L1.64273 10.5841Z"
									fill="currentColor"
								/>
							</svg>

							<span :class="[{ faded: folder.boards.length === 0 }]">
								{{ folder.boards.length }}
							</span>

							<svg
								:class="`dropdown dropdown-${folder.id}`"
								@mousedown.prevent="onDropdownClick(folder)"
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M20 13C20.5523 13 21 12.5523 21 12C21 11.4477 20.5523 11 20 11C19.4477 11 19 11.4477 19 12C19 12.5523 19.4477 13 20 13Z"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M4 13C4.55228 13 5 12.5523 5 12C5 11.4477 4.55228 11 4 11C3.44772 11 3 11.4477 3 12C3 12.5523 3.44772 13 4 13Z"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>

							<svg
								class="handle"
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M9 3C7.89543 3 7 3.89543 7 5C7 6.10457 7.89543 7 9 7C10.1046 7 11 6.10457 11 5C11 3.89543 10.1046 3 9 3Z"
									fill="currentColor"
								/>
								<path
									d="M15 3C13.8954 3 13 3.89543 13 5C13 6.10457 13.8954 7 15 7C16.1046 7 17 6.10457 17 5C17 3.89543 16.1046 3 15 3Z"
									fill="currentColor"
								/>
								<path
									d="M9 10C7.89543 10 7 10.8954 7 12C7 13.1046 7.89543 14 9 14C10.1046 14 11 13.1046 11 12C11 10.8954 10.1046 10 9 10Z"
									fill="currentColor"
								/>
								<path
									d="M15 10C13.8954 10 13 10.8954 13 12C13 13.1046 13.8954 14 15 14C16.1046 14 17 13.1046 17 12C17 10.8954 16.1046 10 15 10Z"
									fill="currentColor"
								/>
								<path
									d="M9 17C7.89543 17 7 17.8954 7 19C7 20.1046 7.89543 21 9 21C10.1046 21 11 20.1046 11 19C11 17.8954 10.1046 17 9 17Z"
									fill="currentColor"
								/>
								<path
									d="M15 17C13.8954 17 13 17.8954 13 19C13 20.1046 13.8954 21 15 21C16.1046 21 17 20.1046 17 19C17 17.8954 16.1046 17 15 17Z"
									fill="currentColor"
								/>
							</svg>
						</header>
						<section>
							<small v-if="folder.default">
								Default is a special folder. It cannot be edited/deleted.
							</small>
							<span class="c-folder-select__label">Boards</span>
							<draggable
								:class="[{ empty: folder.boards.length === 0 }]"
								group="board"
								tag="div"
								v-bind="dragOptions"
								:list="folder.boards"
								@change="onBoardChange($event, folder)"
								@start="drag = true"
								@end="onEnd"
								handle=".handle"
							>
								<transition-group
									type="transition"
									:name="!drag ? 'flip-list' : null"
								>
									<ItemBoard
										:processing="processingBoard"
										:active="isBoardActive(board)"
										v-for="board in folder.boards"
										:board="board"
										:key="board.id"
										@onBoardDropdownClick="onBoardDropdownClick"
									>
									</ItemBoard>
								</transition-group>
							</draggable>
						</section>
					</li>
				</transition-group>
			</draggable>
			<PopupFolderOptions
				@onClose="onPopupFolderOptionsClose"
				@onDelete="onFolderDelete"
				@onAddBoard="onBoardAdd"
				:active="!!popupFolderOptionsTarget"
				:target="popupFolderOptionsTarget"
				:folder="popupFolder"
			></PopupFolderOptions>
			<PopupBoardOptions
				@onClose="onPopupBoardOptionsClose"
				@onDelete="onBoardDelete"
				@onClone="onBoardClone"
				:active="!!popupBoardOptionsTarget"
				:target="popupBoardOptionsTarget"
				:board="popupBoard"
			></PopupBoardOptions>
		</div>
	</div>
</template>

<script>
import draggable from 'vuedraggable';
import api from '@swipekit/lib/api';
import InputText from '@swipekit/components/form/input-text';
import Fuse from 'fuse.js';
import ItemBoard from './item-board';
import InputInlineText from '@swipekit/components/form/input-inline-text';

import PopupFolderOptions from '@swipekit/components/popup/popup-folder-options';
import PopupBoardOptions from '@swipekit/components/popup/popup-board-options';

export default {
	components: {
		draggable,
		InputText,
		ItemBoard,
		InputInlineText,
		PopupFolderOptions,
		PopupBoardOptions,
	},

	data: function () {
		return {
			query: '',
			processingFolder: false,
			processingBoard: false,

			currentFolders: [],

			drag: false,

			popupFolder: null,
			popupFolderOptionsTarget: null,

			popupBoard: null,
			popupBoardOptionsTarget: null,

			currentBoardSlug: '',

			activeFolderIds: [],
		};
	},

	watch: {
		trigger: function (e) {
			this.fetchData();
			if (e) {
				this.openSomeFolders();
			} else {
				this.activeFolderIds = [];
			}
		},
		folders: {
			handler: function () {
				this.$nextTick(() => {
					this.fetchData();
				});
			},
			deep: true,
		},
		boards: {
			handler: function () {
				this.$nextTick(() => {
					this.fetchData();
				});
			},
			deep: true,
		},
		$route: function () {
			if (this.$route.path.includes('/boards/')) {
				this.currentBoardSlug = this.$route.params.slug;
			} else {
				this.currentBoardSlug = '';
			}
		},
	},

	props: {
		trigger: {},
	},

	computed: {
		dragOptions() {
			return {
				animation: 200,
				group: 'description',
				disabled: false,
				ghostClass: 'ghost',
			};
		},
		workspace: function () {
			return this.$store.getters['workspaces/all'];
		},
		user: function () {
			return this.$store.getters['users/user'];
		},
		isAdmin: function () {
			return this.$store.getters['users/isAdmin'];
		},
		searchResults: function () {
			let query = this.query;

			if (!query) {
				return [];
			}

			let boards = this.boards;

			let options = {
				keys: ['name'],
			};
			let fuse = new Fuse(boards, options);
			let items = fuse.search(this.query);
			items = items.map((i) => {
				return i.item;
			});
			return items;
		},
		computedFolders: function () {
			let folders = this.currentFolders;

			try {
				folders = JSON.parse(JSON.stringify(folders));
			} catch (err) {
				folders = [];
			}
			folders = folders.map((f) => {
				f.boards = f.boards || [];
				return f;
			});

			// Only do this for non-admin users
			if (!this.isAdmin) {
				folders = folders.filter((folder) => {
					if (folder.hidden && folder.userId !== this.user.id) {
						return false;
					}

					return true;
				});
			}

			return folders;
		},
		init: function () {
			return this.$store.getters['app/init'];
		},
		boards: function () {
			let boards = this.$store.getters['boards/filtered'];

			return boards;
		},
		folders: function () {
			let folders = this.$store.getters['folders/filtered'] || [];

			return folders;
		},
	},

	methods: {
		isFolderCurrent: function (folder) {
			if (!this.currentBoardSlug) {
				return;
			}

			let boards = folder.boards;
			if (boards.length === 0) {
				return;
			}

			let condition = false;

			for (let i = 0; i < boards.length; i++) {
				let board = boards[i];
				if (board.slug === this.currentBoardSlug) {
					condition = true;
					break;
				}
			}

			return condition;
		},
		isBoardActive: function (board) {
			if (!this.currentBoardSlug) {
				return;
			}

			if (this.currentBoardSlug === board.slug) {
				return true;
			}

			return;
		},
		async onFolderDelete(folder) {
			if (this.processingFolder) {
				return;
			}
			this.processingFolder = true;

			if (folder.boards.length === 0) {
				await this.$store.dispatch('folders/remove', folder.id);
			} else {
				this.$store.dispatch('folders/setDeleteConfirmationId', folder);
				// show a delete modal
			}
			this.processingFolder = false;
		},
		async onBoardDelete(board) {
			if (this.processingBoard) {
				return;
			}
			this.processingBoard = true;

			let hasAds = !!board._count;

			if (!hasAds) {
				await this.$store.dispatch('boards/remove', board.id);
				this.$store.dispatch('message/sendMessage', {
					message: `Board deleted`,
					timer: 1000,
				});
			} else {
				this.$store.dispatch('boards/setDeleteConfirmationId', board);
				// show a delete modal
			}
			this.processingBoard = false;
		},
		async onBoardClone(board) {
			this.processingBoard = true;
			await this.$store.dispatch('boards/setClone', board.id);
			this.$store.dispatch('message/sendMessage', {
				message: `Board cloned!`,
				timer: 2000,
				status: 'success',
			});
			this.processingBoard = false;
		},
		onBoardDropdownClick: function (board) {
			let thisPopupBoard = this.popupBoard;
			setTimeout(() => {
				if (thisPopupBoard) {
					this.popupBoardOptionsTarget = null;
					this.thisPopupBoard = null;
				} else {
					let el = document.querySelector(`.board-${board.id}`);
					this.popupBoard = board;
					this.popupBoardOptionsTarget = el;
				}
			}, 1);
		},
		onDropdownClick: function (folder) {
			let thisPopupFolder = this.popupFolder;
			setTimeout(() => {
				if (thisPopupFolder) {
					this.popupFolderOptionsTarget = null;
					this.popupFolder = null;
				} else {
					let el = document.querySelector(`.dropdown-${folder.id}`);
					this.popupFolder = folder;
					this.popupFolderOptionsTarget = el;
				}
			}, 1);
		},
		onPopupFolderOptionsClose: function () {
			this.closePopup();
		},
		onPopupBoardOptionsClose: function () {
			this.closeBoardPopup();
		},
		closePopup: function () {
			this.$nextTick(() => {
				this.popupFolderOptionsTarget = null;
				this.popupFolder = null;
			});
		},
		closeBoardPopup: function () {
			this.$nextTick(() => {
				this.popupBoardOptionsTarget = null;
				this.popupBoard = null;
			});
		},
		async onBoardAdd(folder) {
			this.closePopup();
			if (this.processingBoard) {
				return;
			}
			this.processingBoard = true;
			this.makeFolderActive(folder);
			const board = await this.$store.dispatch('boards/add', {
				name: this.getNewBoardName(folder),
				folderId: folder.id,
			});
			this.processingBoard = false;
		},
		async onFolderAdd() {
			if (this.processingFolder) {
				return;
			}
			this.processingFolder = true;
			const folder = await this.$store.dispatch('folders/add', {
				name: this.getNewFolderName(),
			});
			this.processingFolder = false;
		},
		async onFolderNameUpdate(e, folder) {
			if (this.processingFolder) {
				return;
			}
			this.processingFolder = true;
			await this.$store.dispatch('folders/update', {
				id: folder.id,
				name: e,
			});
			this.processingFolder = false;
		},
		async onFolderChange(e) {
			if (this.processingFolder) {
				return;
			}
			this.processingFolder = true;
			if (e.moved) {
				this.setFolderOrder();
			}
			this.processingFolder = false;
		},
		getNewFolderName: function () {
			let folders = this.folders.filter((f) => {
				if (f.name.startsWith('New Folder')) {
					return true;
				}

				return false;
			});

			if (folders.length === 0) {
				return `New Folder`;
			}

			return `New Folder (${folders.length + 1})`;
		},
		getNewBoardName: function (folder) {
			let boards = folder.boards || [];
			boards = boards.filter((f) => {
				if (f.name.startsWith('New Board')) {
					return true;
				}

				return false;
			});

			if (boards.length === 0) {
				return `New Board`;
			}

			return `New Board (${boards.length})`;
		},
		setFolderOrder: function () {
			let newArr = [];
			this.currentFolders.map((b, i) => {
				newArr.push({
					id: b.id,
					order: i,
				});
			});

			const obj = {
				folders: newArr,
			};

			this.$store.dispatch('folders/setOrder', obj);
		},
		onHeaderClick: function (e, folder) {
			const isOnHandle = e.target.closest('.handle');
			if (isOnHandle) {
				return;
			}

			const isOnBtn = e.target.closest('.btn');
			if (isOnBtn) {
				return;
			}

			const isDropdown = e.target.closest('.dropdown');
			if (isDropdown) {
				return;
			}

			this.toggleFolderActiveStatus(folder);
		},
		makeFolderActive: function (folder) {
			let index = this.activeFolderIds.indexOf(folder);
			if (index === -1) {
				this.activeFolderIds.push(folder.id);
			}
		},
		toggleFolderActiveStatus: function (folder) {
			let folderId = folder.id;
			let index = this.activeFolderIds.indexOf(folderId);
			if (this.activeFolderIds.indexOf(folderId) === -1) {
				this.activeFolderIds.push(folderId);
			} else {
				this.activeFolderIds.splice(index, 1);
			}
		},
		async onSearchBoardChange(e, folder) {
			console.log(e);
		},
		async onBoardChange(e, folder) {
			if (!folder) {
				return;
			}
			if (e.added) {
				let isBoardInFolderAlready = false;
				for (let i = 0; i < folder.boards.length; i++) {
					let b = folder.boards[i];
					if (b.id === e.added.element.id) {
						isBoardInFolderAlready = true;
						break;
					}
				}

				// board is already in folder so..
				if (isBoardInFolderAlready) {
				}
			}
			if (this.processingBoard) {
				return;
			}
			this.processingBoard = true;
			if (e.added) {
				let board = {
					id: e.added.element.id,
					folderId: folder.id,
					order: e.added.newIndex,
				};
				// also remove the board from the currentfolder and add it to the new folder
				await this.$store.dispatch('boards/update', board);
			}
			if (e.moved) {
				await this.setBoardOrder(folder);
			}
			this.processingBoard = false;
		},
		onUpdate: function (e) {
			let folder = {
				id: e.element.id,
				order: e.newIndex,
			};

			this.updateFolder(folder);
		},
		expand: function (folder) {},
		onClick: function () {
			this.$store.dispatch('app/toggleSidebar');
		},
		onEnd: function (e) {
			this.drag = false;
		},
		async setBoardOrder(folder) {
			let newArr = [];
			folder.boards.map((b, i) => {
				newArr.push({
					id: b.id,
					order: i,
				});
			});

			const obj = {
				boards: newArr,
			};

			await this.$store.dispatch('boards/setOrder', obj);
		},
		async updateFolder(folder) {
			// update the damn folder!
		},
		updateBoard: function (board) {
			this.$store.dispatch('boards/setEdit', board.id);
		},

		getBoardsForFolder: function (f) {
			let boards = this.boards;

			f.boards = [];

			for (let i = 0; i < boards.length; i++) {
				let board = boards[i];
				if (board.folderId && board.folderId === f.id) {
					f.boards.push(board);
				}
			}

			return f.boards;
		},

		fetchData: function () {
			let folders = [];
			try {
				folders = JSON.parse(JSON.stringify(this.folders));
			} catch (err) {
				folders = [];
			}
			folders = folders.map((f) => {
				f.active = false;
				f.boards = this.getBoardsForFolder(f);
				return f;
			});
			this.currentFolders = folders;
		},
		openSomeFolders: function () {
			const folders = this.computedFolders;

			// User has only default folder, so open it by default
			if (folders.length === 1) {
				this.activeFolderIds.push(folders[0].id);
				return;
			}

			if (this.$route.path.includes('/boards/')) {
				const boardSlug = this.$route.params.slug;
				for (let i = 0; i < folders.length; i++) {
					let folder = folders[i];

					let isInBoard = false;

					for (let j = 0; j < folder.boards.length; j++) {
						let board = folder.boards[j];

						if (board.slug === boardSlug) {
							isInBoard = true;
							break;
						}
					}

					if (isInBoard) {
						this.activeFolderIds.push(folder.id);
						break;
					}
				}
			}
		},
	},

	mounted: function () {},
};
</script>

<style lang="scss">
.c-folder-select {
	width: 100%;
	height: 100%;

	.flip-list-move {
		//transition: transform 100ms;
	}

	.no-move {
		transition: transform 1000ms;
	}

	.ghost {
		&:after {
			content: '';
			position: absolute;
			top: -1px;
			left: 2px;
			width: calc(100% - 4px);
			height: 2px;
			background-color: hsl(calc(var(--p) - 32), 65%, 62%);
			//box-shadow: 0 0 4px 0 hsl(calc(var(--p) - 32), 60%, 40%);
			border-radius: 2px;
		}
	}

	.sortable-chosen {
		position: relative;
	}

	.list-group {
		margin: 0;
		padding: 0;
	}

	.list-group-item {
		position: relative;
		display: block;
	}

	&__header {
		display: flex;
		align-items: center;
		padding: var(--margin) var(--margin-lg);

		&:first-of-type {
			padding-bottom: 0;
		}

		.c-input-text {
			width: 100%;
			padding-bottom: 0;

			.form-control {
				border-color: transparent;
				background-color: var(--color-bg-95);
				font-size: var(--font-size-xs);

				&:focus {
					border-color: var(--color-bg-95);
					box-shadow: 0 0 0 2px hsla(var(--p), 5%, 60%, 1) !important;
				}
			}
		}

		pre {
			display: block;
			color: var(--color-button-font);
			font-size: 9px;
		}

		.c-spinner {
			width: 12px;
			height: 12px;
			margin-left: 4px;
		}

		h6 {
			text-transform: initial;
			font-weight: 400;
			letter-spacing: 0;
			color: var(--color-button-font);
			line-height: 1;
			margin-bottom: 0;
		}
	}

	.btn {
		width: 20px;
		height: 20px;
		margin-left: auto;
		padding: 4px !important;
		color: var(--color-button-font);
		background-color: hsl(var(--p), 5%, 20%);
		box-shadow: inset 0 -1px 1px #00000001, inset 0 1px 1px #ffffff05,
			inset 0 5px 20px 1px #ffffff05, 0 2px 5px #3c42570a, 0 1px 1px #0000000f,
			0 0 0 1px #00000013;

		&:hover,
		&:active,
		&:focus {
			color: var(--color-button-font) !important;
			background-color: hsl(var(--p), 5%, 25%) !important;
		}
	}

	&__search {
		padding: 0 var(--margin-lg);
	}

	&__folders {
		padding: var(--margin-lg);
		padding-top: 0;

		> pre {
			color: var(--color-button-font);
		}
	}

	&__item {
		margin-bottom: 0;
		color: var(--color-button-font);
		overflow: hidden;
		position: relative;
		font-size: var(--font-size-xs);

		&:after {
			content: '';
			position: absolute;
			top: 6px;
			left: -4px;
			width: 8px;
			height: 20px;
			background-color: var(--color-primary);
			border-radius: 8px;
			opacity: 0;
		}

		&.current {
			&:after {
				opacity: 1;
			}
		}

		header {
			display: flex;
			align-items: center;
			padding: 4px;
			user-select: none;
			cursor: pointer;

			width: 100%;
			position: relative;
			display: flex;
			align-items: center;
			padding: var(--margin);
			font-size: 14px;
			text-decoration: none !important;
			border-radius: 6px;
			color: rgba(255, 255, 255, 0.9);
			background-color: var(--color-bg-97);
			border-radius: 6px;
			user-select: none;

			svg {
				min-width: 24px;
				height: 24px;
				padding: 4px;
				cursor: pointer;
				&:first-of-type {
					//transform: scale(0.75);
				}
				&:last-of-type {
					cursor: move;
					opacity: 0.35;
				}

				&.dropdown {
					opacity: 0.35;
				}
			}

			> span {
				&:last-of-type {
					margin-left: auto;
					padding: 2px 4px;
					font-feature-settings: 'case' 0, 'dlig' 0, 'frac' 0, 'dnom' 0,
						'numr' 0, 'subs' 0, 'sups' 0, 'tnum', 'zero' 0, 'ss01', 'ss02' 0,
						'ss03' 0, 'ss04', 'ss05' 0, 'ss06' 0, 'ss07' 0, 'ss08' 0, 'cv01' 0,
						'cv02' 0, 'cv03' 0, 'cv04' 0, 'cv05' 0, 'cv06' 0, 'cv07' 0, 'cv08' 0,
						'cv09' 0, 'cv10' 0, 'cv11' 0, 'cv12' 0, 'cv13' 0, 'cpsp' 0, 'c2sc' 0,
						'salt' 0, 'aalt' 0, 'calt', 'ccmp', 'locl', 'kern';
					font-size: var(--font-size-xs);
					line-height: 1;
				}

				&.faded {
					opacity: 0.35;
				}
			}

			.c-input-inline-text {
				padding-left: 0;
				line-height: 1.3;
				font-size: var(--font-size-sm);
				opacity: 0.9;
				letter-spacing: 0.25px;
				cursor: text;

				&:hover {
					.c-input-inline-text__display {
						background-color: rgba(255, 255, 255, 0.1);
					}
				}
			}

			&:hover,
			&:active {
				background-color: rgba(255, 255, 255, 0.1);

				svg {
					&:last-of-type {
						opacity: 1;
					}

					&.dropdown {
						opacity: 1;
					}
				}
			}
		}

		section {
			display: none;
			padding: 4px;
			padding-left: 28px;

			> small {
				display: block;
				margin-bottom: var(--margin);
				line-height: 1.4;
				opacity: 0.75;
			}

			> div {
				> span {
					display: block;
					min-height: 36.5px;
				}

				&.empty {
					position: relative;

					&:after {
						content: 'Drag boards in here';
						text-align: center;
						font-size: var(--font-size-xs);
						display: flex;
						align-items: center;
						justify-content: center;
						opacity: 0.85;
						position: absolute;
						top: 0;
						left: 0;
						width: 100%;
						height: 100%;
						border: dashed 2px hsl(var(--p), 5%, 35%);
						border-radius: var(--border-radius);
					}
				}
			}
		}

		&.active {
			header {
				background-color: hsl(var(--p), 5%, 22%);

				svg {
					opacity: 1;

					&:first-of-type {
						//transform: rotate(90deg) scale(0.75);
					}
				}
			}
			section {
				display: block;
			}
		}
	}

	&__label {
		display: block;
		font-size: var(--font-size-xs);
		opacity: 0.75;
	}

	&__empty {
		display: block;
		font-size: var(--font-size-xs);
	}

	&__empty-state {
		text-align: center;
		padding: var(--spacer);

		> p {
			opacity: 0.75;
			margin-bottom: var(--margin);
		}
	}
}
</style>
